import type {
  DemoState,
  RegionEntry,
  RegionTemplateBinding,
  RegionTemplateGroup,
} from '../state';

export const TEMPLATE_BINDING_CANDIDATE_LIMIT = 3;
export const TEMPLATE_BINDING_CANDIDATE_MAX_DISTANCE = 48;

export function rgbDistanceSq(
  a: [number, number, number],
  b: [number, number, number],
): number {
  const dr = a[0] - b[0];
  const dg = a[1] - b[1];
  const db = a[2] - b[2];
  return dr * dr + dg * dg + db * db;
}

function candidateRegionUuids(
  regions: readonly RegionEntry[],
  rgb: [number, number, number],
): string[] {
  const maxDistanceSq =
    TEMPLATE_BINDING_CANDIDATE_MAX_DISTANCE *
    TEMPLATE_BINDING_CANDIDATE_MAX_DISTANCE;
  return regions
    .map((region) => ({
      uuid: region.uuid,
      distanceSq: rgbDistanceSq(region.rgb, rgb),
    }))
    .filter(
      (candidate) =>
        candidate.distanceSq > 0 && candidate.distanceSq <= maxDistanceSq,
    )
    .sort((a, b) => a.distanceSq - b.distanceSq)
    .slice(0, TEMPLATE_BINDING_CANDIDATE_LIMIT)
    .map((candidate) => candidate.uuid);
}

export function bindTemplateGroupsForImport(
  state: DemoState,
  groups: readonly RegionTemplateGroup[],
  _baseBindings: readonly RegionTemplateBinding[],
): {
  bindings: RegionTemplateBinding[];
  regionEntries: RegionEntry[];
} {
  let nextEntries = state.regionEntries;
  const bindings = groups.map((group) => {
    const exact = state.regionEntries.find(
      (entry) => rgbDistanceSq(entry.rgb, group.rgb) === 0,
    );
    const boundRegion = exact ?? null;
    if (boundRegion !== null && group.preset !== null) {
      nextEntries = nextEntries.map((entry) =>
        entry.uuid === boundRegion.uuid ? { ...entry, override: group.preset } : entry,
      );
    }
    return {
      templateRegionId: group.templateRegionId,
      rgb: group.rgb,
      stackId: group.stackId,
      boundRegionUuid: boundRegion?.uuid ?? null,
      boundLabel: boundRegion?.label ?? null,
      candidateRegionUuids:
        exact === undefined
          ? candidateRegionUuids(state.regionEntries, group.rgb)
          : [],
    };
  });
  return { bindings, regionEntries: nextEntries };
}
